import { makeObservable, observable, flow, computed, action } from "mobx";
import { IOrganizationRelationListingModel, OrganizationRelationListingModel, IOrganizationListingModel, IUserModel, UserModel, IBlobSasModel, ILicenseTypeModel, IVeterinarianModel, VeterinarianModel, ClientConfigurationModel, LicenseModel } from "@common/models";
import { IAuthRoleModel } from "@common/models/Security";
import { AzureBlobStore, NotificationStoreInstance } from "@common/stores";
import { BlobEntityType } from "@common/models/Enums";
import { HospitalOrganizationRelationListingModel } from "@models/HospitalOrganizationRelationListingModel";
import { ILicenseRequestModel } from "@common/models/requests";
import { chain, cloneDeep, differenceBy, uniqBy, without } from "lodash";
import { UserService } from "@common/services/userService";
import { LicenseService, VeterinarianService } from "@common/services";
import { ILicenseModelWithNewLicenseImage } from "@models/LicenseModelWithNewLicenseImage";

const defaultUser: UserModel = new UserModel({});

const defaultVet: IVeterinarianModel = {
    veterinarianId: 0,
    licenses: [],
    firstName: "",
    lastName: "",
    email: "",
    title: "",
    username: "",
    hospitalSchedules: [],
    rowVersion: '',
    schedulingColor: "",
    petcoUserId: "",
    activeSignature: {
        veterinarianSinatureId: 0,
        veterinarianId: 0,
        photoId: 0,
        active: false,
        rowVersion: "",
        signature: {
            photoId: 0,
            photoUrl: "",
            container: "",
            fileName: "",
            processedPhotoBaseUrl: ""
        }
    },
    schedulePreferences: [],
    fullName: undefined
}

export interface IUserStoreInit extends Partial<UserStore> {
    configuration: ClientConfigurationModel;
    strUserId?: string | undefined; //exists so memos can key off userId to recreate the user store
}
export class UserStore {
    blobStorageStore: AzureBlobStore = new AzureBlobStore({ containerName: BlobEntityType.License });
    @observable configuration: ClientConfigurationModel;
    @observable licensesAreBeingUpdated: boolean = false;
    @observable showSignatureModal: boolean = false;
    @observable signatureIsBeingUpdated: boolean = false;
    @observable licenseAddModalVisible: boolean = false;
    @observable licenseDeleteModalVisible: boolean = false;
    @observable userLoading: boolean = true;
    @observable licenseDeletePending: boolean = false;
    @observable licenseToDelete: LicenseModel | undefined = undefined;
    @observable user: UserModel = defaultUser;
    @observable vet: VeterinarianModel = new VeterinarianModel(defaultVet);
    @observable userId: number | undefined;

    @observable licenseModel: ILicenseRequestModel = {
        licenseId: 0,
        number: "",
        expirationDate: new Date().toISOString(),
        stateId: 0,
        veterinarianId: 0,
        licenseTypeId: 0,
        licensePhoto: {
            container: "",
            fileName: "",
            photoUrl: "",
        }
    }

    constructor(init: IUserStoreInit) {
        this.configuration = init.configuration;
        if (init.strUserId != null && init.strUserId.length > 0)
            this.userId = parseInt(init.strUserId);

        makeObservable(this, undefined, {
            autoBind: true
        }); //use decorators     
    }

    private onlySwiftRoles = (r: IAuthRoleModel) => !r.description.toLowerCase().startsWith("petpass");

    @computed get implicitRoles() {
        return chain(this.user.implicitAuthRoles || []).filter(this.onlySwiftRoles).orderBy('description').value();
    }

    @computed get explicitRoles() {
        return chain(this.user.explicitAuthRoles || []).filter(this.onlySwiftRoles).orderBy('description').value();
    }

    @computed get assignedRoles() {
        return this.implicitRoles.concat(this.explicitRoles);
    }

    @computed get availableRoles() {
        return differenceBy(this.configuration.authRoles, this.assignedRoles, 'authRoleId');
    }
    @computed get userHospitalOrganizations() {
        if (this.user.hospitalOrganizations && this.user.hospitalOrganizations.length) {
            return this.user.hospitalOrganizations.map(value => {
                //there's two hospital organization relation listing model, the base one in common, and one in hospital that adds hospital properties like isReadyOnly.
                //this code instantiates the hospital model from the common model's values and adds the needed corpOrgModel for hospitals version to make the isReadOnly check work.
                return new HospitalOrganizationRelationListingModel({ ...value, corpOrgModel: this.configuration.currentCorpOrganization });
            });
        }
        return [];
    }
    @computed get hasVetRole() {
        const licensedRoles = ["Veterinarian", "Veterinary Technician"];
        return chain(this.assignedRoles).some(r => licensedRoles.includes(r.description)).value();
    }
    @computed get hasLicenses() {
        return this.vet?.licenses !== undefined && this.vet?.licenses.length > 0;
    }
    @computed get sortedLicenses() {
        return chain(this.vet?.licenses || []).sortBy('expirationDate', 'state.abbreviation', 'licenseType.name').value();
    }
    @computed get licenseTypes(): ILicenseTypeModel[] {
        return this.configuration.licenseTypes;
    }

    /**
     * Returns description used when deleting an existing license
     */
    @computed get licenseDeleteDescription() {
        return "Are you sure you wish to delete this license record?"
    }

    @computed get isActive() {
        return this.user.isProfileActive
    }

    /**
     * Updates model of license to be deleted
     * @param license updated license properties on actionable license for deletion
     */
    @action.bound updateLicenseToDelete(license: LicenseModel | undefined) {
        this.licenseToDelete = license;
    }


    /**
     * Filter the users with the existing search params.
     */
    refreshUser = flow(function* (this: UserStore, userId: number): Generator<Promise<any>, void, any> {
        this.userLoading = true;
        yield this.getUserImpl(userId);
        this.userLoading = false;
    });

    /**
     * Used in multiple places in here, updates a user with only the data that needs updated for the request
     */
    updateUser = flow(function* (this: UserStore, user: Partial<IUserModel>, successMsg: string, failureMessage: string): Generator<Promise<any>, void, any> {
        this.userLoading = true;

        yield UserService.updateUser(this.user.userId, { ...this.user, ...user })
            .then(NotificationStoreInstance.onSuccess(successMsg))
            .catch(NotificationStoreInstance.onError(failureMessage, undefined));

        yield this.getUserImpl(this.user.userId);

        this.userLoading = false;
    })

    getUserImpl = flow(function* (this: UserStore, userId: number): Generator<Promise<any>, void, any> {
        this.user = yield UserService.getUser(userId)
            .catch(NotificationStoreInstance.onError("Failed to retrive user record", defaultUser));

        if (this.hasVetRole) {
            this.vet = yield VeterinarianService.getVeterinarianByUserId(userId)
                .catch(() => NotificationStoreInstance.onError("Failed to retrieve veterinarian record", undefined));
        }
    })

    //moved these to actions and moved the User Update post to a single flow to be re-used by addHospitals
    @action.bound addRoles(roleIds: number[]) {
        const roles = this.configuration.authRoles.filter((r) => roleIds.includes(r.authRoleId));
        const explicitAuthRoles = this.user.explicitAuthRoles.concat(roles);
        this.updateUser({ explicitAuthRoles }, "Role added", "Failure to add role");
    }

    @action.bound deleteRole(roleToDelete: IAuthRoleModel) {
        const explicitAuthRoles = without(this.user.explicitAuthRoles, roleToDelete);
        this.updateUser({ explicitAuthRoles }, "Role removed", "Failure to remove role");
    }

    @action.bound showAddLicenseModal(shouldShow: boolean) {
        this.licenseAddModalVisible = shouldShow;
    }


    addOrganizations = flow(function* (this: UserStore, hospitalOrgs: IOrganizationListingModel[]): Generator<Promise<any>, void, any> {
        const currentOrgRelations = cloneDeep(this.user.organizations);
        const newOrgRelations: IOrganizationRelationListingModel[] = [];
        const newHospitalOrgs = hospitalOrgs.filter((o) => {
            let matches = currentOrgRelations.filter((co) => co.organizationId === o.organizationId);
            return matches.length === 0;
        });

        newHospitalOrgs.forEach((newOrg) => {
            newOrgRelations.push(OrganizationRelationListingModel.CreateForUserOrgRelation(this.user, newOrg));
        });

        //prevent dupes from being added in the event that happened
        const uniqNewOrgRelations = uniqBy(newOrgRelations, (o) => o.organizationId);

        if (newOrgRelations.length > 0) {
            let combinedRelations = currentOrgRelations.concat(uniqNewOrgRelations);
            yield this.updateUser({ organizations: combinedRelations }, "Location added", "Failure to add location");
        }
    })

    deleteOrganization = flow(function* (this: UserStore, hospitalOrg: IOrganizationRelationListingModel): Generator<Promise<any>, void, any> {
        //hospital org is extended and has a hospitalCount field, so doing without on it won't work, need to get the non extended 
        //hospital org from user.organizations from the id of the extended one and then delete that.
        const orgToDelete = this.user.organizations.filter(value => value.organizationRelationId === hospitalOrg.organizationRelationId)[0];
        const updateOrganizations = without(this.user.organizations, orgToDelete);
        yield this.updateUser({ organizations: updateOrganizations }, "Location removed", "Failure to remove location");
    });



    deleteLicense = flow(function* (this: UserStore) {
        if (!this.licenseToDelete || this.licenseToDelete.isReadOnly)
            return;

        yield LicenseService.deleteLicense(this.licenseToDelete.licenseId)
            .then(NotificationStoreInstance.onSuccess("License removed"))
            .catch(NotificationStoreInstance.onError("License failed to be removed", undefined));

        this.updateLicenseToDelete(undefined);
        yield this.refreshUser(this.user.userId);
    });

    addLicense = flow(function* (this: UserStore, licenseToAdd: ILicenseModelWithNewLicenseImage, licensePhotos: FileList) {
        this.licensesAreBeingUpdated = true;

        // upload files to azure
        let blobSas: IBlobSasModel[] = []
        if (licensePhotos.length > 0) {
            blobSas = yield this.blobStorageStore.uploadFiles(licensePhotos);
            if (blobSas.length === 0)
                throw "There was a failure saving photo to azure blog storage";
        }

        // create license request model
        this.licenseModel.expirationDate = licenseToAdd.expirationDate;
        this.licenseModel.licenseId = licenseToAdd.licenseId;
        this.licenseModel.licenseTypeId = licenseToAdd.licenseType.licenseTypeId;
        this.licenseModel.number = licenseToAdd.number;
        this.licenseModel.stateId = licenseToAdd.state.stateId;
        this.licenseModel.veterinarianId = this.vet.veterinarianId;
        if (blobSas.length > 0) {
            this.licenseModel.licensePhoto.container = blobSas[0].container;
            this.licenseModel.licensePhoto.photoUrl = blobSas[0].sasUrl;
            this.licenseModel.licensePhoto.fileName = blobSas[0].fileName;
        }

        yield LicenseService.addLicense(this.licenseModel)
            .then(NotificationStoreInstance.onSuccess("License added"))
            .catch(NotificationStoreInstance.onError("Failed to add license", undefined))

        this.licensesAreBeingUpdated = false;
        this.showAddLicenseModal(false);
        yield this.refreshUser(this.user.userId);
    });

    updateLicense = flow(async function* (this: UserStore, license: ILicenseModelWithNewLicenseImage, newPhotos: FileList) {
        this.licensesAreBeingUpdated = true;

        const blobStore = new AzureBlobStore({ containerName: BlobEntityType.License });
        let sasResults: IBlobSasModel[] | undefined = [];

        const updateRequest: Partial<ILicenseRequestModel> = {
            licenseId: license.licenseId,
            number: license.number,
            expirationDate: license.expirationDate,
            stateId: license.state.stateId,
            licenseTypeId: license.licenseType.licenseTypeId
        }

        //upload new image to blob storage if new image was selected
        if (newPhotos && newPhotos.length > 0) {
            sasResults = await blobStore.uploadFiles(newPhotos);
        }

        //change photo if new photo selected
        if (sasResults && sasResults.length > 0) {
            updateRequest.licensePhoto = {
                photoUrl: sasResults[0].sasUrl,
                container: sasResults[0].container,
                fileName: sasResults[0].fileName
            };
        }

        yield LicenseService.updateLicense(license.licenseId, updateRequest)
            .then(NotificationStoreInstance.onSuccess('License Updated'))
            .catch(NotificationStoreInstance.onError("Failure to update license", undefined));

        yield this.refreshUser(this.user.userId);

        this.licensesAreBeingUpdated = false;
    });
}
